<script setup lang="ts">
import {
  ref,
  computed,
  onMounted,
  onUnmounted,
  onActivated,
  onDeactivated,
  watch,
  nextTick,
  useId,
} from "vue";
import { Skeleton } from "@/components/ui/skeleton";
import CardErrorState from "@/components/ui/CardErrorState.vue";
import RadarLegend from "@/components/weather/RadarLegend.vue";
import WindLegend from "@/components/weather/WindLegend.vue";
import CollapsibleLegend from "@/components/weather/CollapsibleLegend.vue";
import MapZoomControls from "@/components/map/MapZoomControls.vue";
import MapPlaybackControls from "@/components/map/MapPlaybackControls.vue";
import { MapPinOff, SlidersHorizontal, X } from "@lucide/vue";
import type { MapPreferences } from "@/stores/settings.store";
import { useGoogleMap } from "@/composables/useGoogleMap";
import { MAP_ZOOM_DEFAULT, MAP_ZOOM_MIN_RECENTER } from "@/constants/map";
import { useHybridRadar } from "@/composables/useHybridRadar";
import type { RadarSource } from "@/utils/radarProvider";
import { useWindParticles } from "@/composables/useWindParticles";
import type { ROLocation } from "rainowls-shared";

const props = defineProps<{
  wallMode?: boolean;
  location: ROLocation;
  preferences: MapPreferences;
  error?: boolean;
}>();

const emit = defineEmits<{
  toggleRadar: [];
  toggleWind: [];
  radarOpacity: [value: number];
  windOpacity: [value: number];
  radarSource: [source: RadarSource | undefined];
}>();

const controlsOpen = ref(false);
const controlsPanel = ref<HTMLElement | null>(null);
const controlsToggle = ref<HTMLButtonElement | null>(null);
const settingsOpen = ref(false);
const controlsId = useId();
const controlsVisible = computed(() => !props.wallMode || controlsOpen.value);
let idleTimer: ReturnType<typeof setTimeout> | undefined;
let pointerHeld = false;
let keyboardInteraction = false;

const zoomModifier = ref("Ctrl");
const radarOpacity = computed(() => props.preferences.radarOpacity);
const showRadarLayer = computed(() => props.preferences.showRadarLayer);

const windParticleOpacity = computed(
  () => props.preferences.windParticleOpacity,
);

const {
  isWindLayerActive,
  initOverlay: initWindOverlay,
  onMapIdle: onWindMapIdle,
  suspend: suspendWind,
  resume: resumeWind,
  cleanup: cleanupWind,
} = useWindParticles(() => props.preferences);

const mapContainer = ref<HTMLElement | null>(null);
let locationOverlay: google.maps.OverlayView | null = null;

const {
  timestamps: radarTimestamps,
  currentFrameIndex,
  isPlaying,
  timeDisplay,
  fetchTimestamps,
  initLayers,
  switchSource,
  togglePlayback,
  nextFrame,
  prevFrame,
  goToLatest,
  isTilesLoading,
  activeSource,
  suspend: suspendRadar,
  resume: resumeRadar,
  cleanup: cleanupRadar,
} = useHybridRadar(
  () => props.preferences,
  () => props.preferences.radarSourceOverride,
);

const progressPercent = computed(() => {
  if (radarTimestamps.value.length <= 1) return 100;
  return (currentFrameIndex.value / (radarTimestamps.value.length - 1)) * 100;
});

const location = computed(() => props.location);

const {
  map,
  isLoading,
  initMap,
  zoomIn,
  zoomOut,
  cleanup: cleanupMap,
} = useGoogleMap({
  containerRef: mapContainer,
  initialZoom: MAP_ZOOM_DEFAULT,
  // Let normal scrolling reach the other Home sections.
  gestureHandling: "cooperative",
  center: location,
  onReady: onMapReady,
  onIdle: () => onWindMapIdle(),
  beforeRecreate: () => {
    cleanupRadar();
    cleanupWind();
    if (locationOverlay) {
      locationOverlay.setMap(null);
      locationOverlay = null;
    }
  },
});

let mounted = false;
let radarMetadataReady = false;

/** Schedule dismissal only while controls are idle and not keyboard-focused. */
function scheduleHide() {
  clearTimeout(idleTimer);
  if (
    !props.wallMode ||
    !controlsOpen.value ||
    settingsOpen.value ||
    pointerHeld
  )
    return;
  const focused = document.activeElement;
  if (
    keyboardInteraction &&
    focused &&
    (controlsPanel.value?.contains(focused) || controlsToggle.value === focused)
  )
    return;
  idleTimer = setTimeout(() => closeControls(), 10_000);
}

/** Dismiss only UI controls; the map and radar playback remain mounted. */
function closeControls(restoreFocus = false) {
  clearTimeout(idleTimer);
  controlsOpen.value = false;
  settingsOpen.value = false;
  pointerHeld = false;
  if (restoreFocus) controlsToggle.value?.focus();
}

/** Toggle the wall-mode overlay from its persistent touch target. */
function toggleControls() {
  if (controlsOpen.value) closeControls();
  else {
    controlsOpen.value = true;
    scheduleHide();
  }
}

/** Outside taps dismiss controls; holding a control pauses auto-hide. */
function onPointerDown(event: PointerEvent) {
  keyboardInteraction = false;
  if (!props.wallMode || !controlsOpen.value) return;
  const target = event.target as Node;
  if (controlsToggle.value?.contains(target)) return;
  if (!controlsPanel.value?.contains(target)) closeControls();
  else {
    pointerHeld = true;
    clearTimeout(idleTimer);
  }
}

/** Restart the idle countdown after a drag or touch finishes anywhere. */
function onPointerUp() {
  pointerHeld = false;
  scheduleHide();
}

/** Keyboard focus keeps controls open; leaving the group restarts the timer. */
async function onFocusChange() {
  await nextTick();
  scheduleHide();
}

/** Keyboard use prevents idle dismissal; Escape restores focus to the toggle. */
function onKeyDown(event: KeyboardEvent) {
  keyboardInteraction = true;
  scheduleHide();
  if (event.key === "Escape" && props.wallMode && controlsOpen.value) {
    event.stopPropagation();
    closeControls(true);
  }
}

/** Keep controls visible throughout an open layer-settings session. */
function onSettingsOpen(open: boolean) {
  settingsOpen.value = open;
  scheduleHide();
}

/** Create the map overlay that marks the selected location. */
function createLocationDotOverlay(
  position: google.maps.LatLng,
  targetMap: google.maps.Map,
): google.maps.OverlayView {
  const overlay = new google.maps.OverlayView();
  let div: HTMLElement | null = null;
  let currentPosition = position;

  // Attach the location dot to the overlay pane.
  overlay.onAdd = function () {
    div = document.createElement("div");
    div.style.position = "absolute";
    div.style.width = "18px";
    div.style.height = "18px";
    div.style.borderRadius = "50%";
    div.style.background = "white";
    div.style.boxShadow = "0 1px 4px rgba(0,0,0,0.4)";
    div.style.display = "flex";
    div.style.alignItems = "center";
    div.style.justifyContent = "center";

    const inner = document.createElement("div");
    inner.style.width = "12px";
    inner.style.height = "12px";
    inner.style.borderRadius = "50%";
    inner.style.background = "#6366f1";
    div.appendChild(inner);

    this.getPanes()?.overlayMouseTarget.appendChild(div);
  };

  // Position the dot using the current map projection.
  overlay.draw = function () {
    if (!div) return;
    const point = this.getProjection()?.fromLatLngToDivPixel(currentPosition);
    if (point) {
      div.style.left = `${point.x - 9}px`;
      div.style.top = `${point.y - 9}px`;
    }
  };

  // Release the dot element when Maps removes the overlay.
  overlay.onRemove = function () {
    div?.parentElement?.removeChild(div);
    div = null;
  };

  // Reuse the existing dot when the selected coordinates change.
  (overlay as any).setPosition = (pos: google.maps.LatLng) => {
    currentPosition = pos;
    overlay.draw();
  };

  overlay.setMap(targetMap);
  return overlay;
}

/** Attach weather overlays after the map is ready. */
function onMapReady(mapInstance: google.maps.Map) {
  updateLocationMarker();
  if (radarMetadataReady) {
    void initLayers(
      mapInstance,
      mapInstance.getCenter()?.lat() ?? 39.8283,
      mapInstance.getCenter()?.lng() ?? -98.5795,
    );
  }
  initWindOverlay(mapInstance);
}

/** Recenter the map on the selected location. */
function centerOnLocation() {
  if (!map.value || !location.value) return;
  map.value.setCenter({ lat: location.value.lat, lng: location.value.lng });
  map.value.setZoom(MAP_ZOOM_MIN_RECENTER);
}

/** Move the existing location marker or create it on first use. */
function updateLocationMarker() {
  if (!map.value || !location.value) return;

  const pos = new google.maps.LatLng(location.value.lat, location.value.lng);

  if (locationOverlay) {
    (locationOverlay as any).setPosition(pos);
  } else {
    locationOverlay = createLocationDotOverlay(pos, map.value);
  }
}

// Changing presentation resets only the disclosure, never the map resources.
watch(
  () => props.wallMode,
  () => closeControls(),
);

// Metadata changes must not reset radar frames or invalidate cached tiles.
watch([() => props.location.lat, () => props.location.lng], ([lat, lng]) => {
  updateLocationMarker();
  if (map.value) switchSource(lat, lng);
});

onMounted(async () => {
  zoomModifier.value = /Mac|iPhone|iPad/.test(navigator.platform)
    ? "⌘"
    : "Ctrl";
  document.addEventListener("pointerdown", onPointerDown);
  document.addEventListener("pointerup", onPointerUp);
  document.addEventListener("pointercancel", onPointerUp);
  document.addEventListener("keydown", onKeyDown);
  mounted = true;
  // Fetch radar frames alongside the basemap, then attach them when ready.
  void fetchTimestamps().then(() => {
    radarMetadataReady = true;
    if (!mounted || !map.value) return;
    const center = map.value.getCenter();
    void initLayers(
      map.value,
      center?.lat() ?? location.value.lat,
      center?.lng() ?? location.value.lng,
    );
  });
  await initMap();
});

onDeactivated(() => {
  suspendRadar();
  suspendWind();
});

onActivated(() => {
  void resumeRadar();
  resumeWind();
});

onUnmounted(() => {
  clearTimeout(idleTimer);
  document.removeEventListener("pointerdown", onPointerDown);
  document.removeEventListener("pointerup", onPointerUp);
  document.removeEventListener("pointercancel", onPointerUp);
  document.removeEventListener("keydown", onKeyDown);
  mounted = false;
  cleanupRadar();
  cleanupWind();
  cleanupMap();
});
</script>

<template>
  <div class="radar-card bg-app-surface">
    <div class="relative h-full overflow-hidden rounded-xl">
      <!-- Map fills entire card -->
      <div
        v-show="!isLoading"
        ref="mapContainer"
        class="absolute inset-0 h-full w-full"
      />

      <!-- Loading Overlay -->
      <div
        v-if="isLoading"
        class="absolute inset-0 z-10 flex items-center justify-center"
      >
        <div class="flex h-full w-full flex-col">
          <div class="flex items-center justify-between px-3 py-2">
            <div class="flex items-center gap-2">
              <Skeleton class="h-4 w-4 rounded" />
              <Skeleton class="h-4 w-12" />
            </div>
            <Skeleton class="h-3 w-10" />
          </div>
          <div class="m-3 mt-0 flex-1">
            <Skeleton class="h-full w-full rounded-lg" />
          </div>
          <div class="flex items-center justify-center gap-2 pb-3">
            <Skeleton class="h-8 w-8 rounded" />
            <Skeleton class="h-8 w-8 rounded border border-white/30" />
            <Skeleton class="h-8 w-8 rounded" />
            <div class="ml-3 flex gap-1">
              <Skeleton
                v-for="i in 5"
                :key="i"
                class="h-1.5 w-1.5 rounded-full"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Error Overlay -->
      <div v-if="props.error" class="absolute inset-0 z-20 bg-app-surface/95">
        <CardErrorState :icon="MapPinOff" message="Radar unavailable" />
      </div>

      <!-- Loading indicator overlay -->
      <div
        v-if="isTilesLoading"
        v-show="!isLoading"
        class="absolute top-0 left-0 z-10 flex items-center gap-1.5 px-3 py-2"
      >
        <div
          class="tile-loading-spinner h-3 w-3 animate-spin rounded-full [border-top-color:var(--muted)] [animation-duration:800ms] [border:2px_solid_var(--edge)]"
        />
        <span class="text-[10px] text-text-secondary">tiles</span>
      </div>

      <div
        :id="controlsId"
        ref="controlsPanel"
        v-if="controlsVisible"
        class="pointer-events-none absolute inset-0 z-10 [&>div]:pointer-events-auto"
        @pointermove="scheduleHide"
        @focusin="onFocusChange"
        @focusout="onFocusChange"
        @keydown="scheduleHide"
      >
        <!-- Zoom Controls -->
        <div
          v-show="!isLoading"
          class="absolute right-3 z-10"
          :class="wallMode ? 'top-3' : 'top-1/2 -translate-y-1/2'"
        >
          <MapZoomControls
            locate-title="Center on location"
            @zoom-in="zoomIn"
            @zoom-out="zoomOut"
            @locate="centerOnLocation"
          />
        </div>

        <!-- Legends (top-left, collapsible) -->
        <div
          v-show="!isLoading"
          class="absolute top-2 left-2 z-10"
        >
          <CollapsibleLegend v-if="showRadarLayer || isWindLayerActive">
            <RadarLegend v-if="showRadarLayer" />
            <WindLegend v-if="isWindLayerActive" :unit="preferences.unit" />
          </CollapsibleLegend>
        </div>

        <p
          v-if="!wallMode && !isLoading && !props.error"
          class="map-gesture-hint pointer-events-none absolute right-3 bottom-3 z-10 w-max max-w-[calc(100%_-_32px)] rounded-small border border-app-border bg-app-material px-2.5 py-1.5 text-center text-[11px] leading-[1.5] text-text-muted max-[1200px]:bottom-16"
        >
          <span
            class="mouse-hint [@media_(hover:_none)_and_(pointer:_coarse)]:hidden"
            ><kbd
              class="font-semibold text-text-primary [font:inherit]"
              :aria-label="zoomModifier === '⌘' ? 'Command' : 'Control'"
              >{{ zoomModifier }}</kbd
            >
            + scroll to zoom</span
          >
          <span
            class="touch-hint hidden [@media_(hover:_none)_and_(pointer:_coarse)]:[display:inline]"
            >Pinch to zoom</span
          >
        </p>

        <!-- Playback Controls -->
        <div
          v-show="!isLoading"
          class="absolute bottom-3 z-10"
          :class="wallMode ? 'left-2 right-16' : 'left-1/2 -translate-x-1/2'"
        >
          <MapPlaybackControls
            :timestamps="radarTimestamps"
            :current-frame-index="currentFrameIndex"
            :is-playing="isPlaying"
            :time-display="timeDisplay"
            :progress-percent="progressPercent"
            :show-radar-layer="showRadarLayer"
            :radar-opacity="radarOpacity"
            :is-wind-layer-active="isWindLayerActive"
            :wind-particle-opacity="windParticleOpacity"
            :radar-source-override="preferences.radarSourceOverride"
            :active-radar-source="activeSource"
            @settings-open-change="onSettingsOpen"
            @prev-frame="prevFrame"
            @play-pause="togglePlayback"
            @next-frame="nextFrame"
            @go-to-latest="goToLatest"
            @toggle-radar="emit('toggleRadar')"
            @toggle-wind="emit('toggleWind')"
            @update-radar-opacity="emit('radarOpacity', $event)"
            @update-wind-opacity="emit('windOpacity', $event)"
            @update-radar-source="emit('radarSource', $event)"
          />
        </div>
      </div>
      <div
        v-if="wallMode && !isLoading && !props.error"
        class="pointer-events-none absolute inset-x-3 bottom-3 z-20 flex items-end justify-between gap-2"
      >
        <span
          v-if="!controlsOpen"
          class="rounded-small border border-app-border bg-app-material px-2 py-1 text-[11px] text-text-muted"
          aria-label="Radar frame time"
          >{{ timeDisplay || "Radar time unavailable" }}</span
        >
        <button
          ref="controlsToggle"
          type="button"
          class="pointer-events-auto ml-auto grid size-11 shrink-0 place-items-center rounded-control border border-app-border bg-app-material text-text-muted hover:text-text-primary focus-visible:outline-2 focus-visible:outline-accent"
          :aria-label="controlsOpen ? 'Hide map controls' : 'Map controls'"
          :aria-expanded="controlsOpen"
          :aria-controls="controlsId"
          @click="toggleControls"
          @focus="onFocusChange"
          @blur="onFocusChange"
        >
          <X v-if="controlsOpen" :size="18" aria-hidden="true" />
          <SlidersHorizontal v-else :size="18" aria-hidden="true" />
        </button>
      </div>
    </div>
  </div>
</template>
